/**
 * PhotoToVideoSheet — bottom sheet «Оживить фото?» (Figma 557:10697).
 *
 * Layout (matches Figma modal / photo-to-video):
 *   - Header row: centered title «Оживить фото?» + subtitle, X close top-right.
 *   - Two quality cards (LQ / MQ), MQ selected by default.
 *   - CTA primary lg «Оживить (N кредита)» + «Баланс: K [coin]» under label.
 *
 * Pricing is fixed per quality. Backend mirrors this rule in
 * backend/routes/video.js.
 *
 * Props:
 *  - open             (bool)    — sheet visibility
 *  - userPlan         ('FREE' | 'PREMIUM' | 'ULTRA') — for paywall gate
 *  - balance          (number)  — current credit balance (for «Баланс: N»)
 *  - srcGenerationId  (string)  — id of source photo Generation row
 *  - onClose          (fn)
 *  - onSubmitted      (fn(generationId)) — called after 202 success; if
 *                                          absent, navigate('/gallery').
 */
import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UIButton, Icon } from '../primitives';
import { usePhotoToVideoMutation } from '../../core/queries/mutations';
import useConsentGuard from '../../hooks/useConsentGuard';
import { useConsentStore } from '../../store/consentStore';
import { getVideoCreditCost } from '../../core/generationPricing';

const QUALITY_OPTIONS = [
  {
    key: 'LQ',
    label: '480p',
    titleKey: 'photoToVideo.lowQuality',
    subtitleKey: 'photoToVideo.lowQualitySubtitle',
    enum: 'Q_480P',
    allowedPlans: ['FREE', 'PREMIUM', 'ULTRA'],
  },
  {
    key: 'MQ',
    label: '720p',
    titleKey: 'photoToVideo.mediumQuality',
    subtitleKey: 'photoToVideo.mediumQualitySubtitle',
    enum: 'Q_720P',
    allowedPlans: ['FREE', 'PREMIUM', 'ULTRA'],
  },
];

const VIDEO_DURATION_SEC = 10;

function pluralize(n, [one, few, many]) {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 14) return many;
  if (mod10 === 1) return one;
  if (mod10 >= 2 && mod10 <= 4) return few;
  return many;
}

export default function PhotoToVideoSheet({
  open,
  userPlan = 'FREE',
  balance = 0,
  srcGenerationId,
  onClose,
  onSubmitted,
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { requireConsent } = useConsentGuard();
  const consentRequired = useConsentStore((state) => state.consentRequired);
  const versionChanged = useConsentStore((state) => state.versionChanged);
  const [selectedKey, setSelectedKey] = useState('MQ');
  const durationSec = VIDEO_DURATION_SEC;
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const photoToVideoMutation = usePhotoToVideoMutation();

  const quality = useMemo(
    () =>
      QUALITY_OPTIONS.find((q) => q.key === selectedKey) || QUALITY_OPTIONS[1],
    [selectedKey],
  );

  const cost = getVideoCreditCost(quality.enum);
  const planAllowsQuality = quality.allowedPlans.includes(userPlan);
  const accountRestrictionsAvailable = consentRequired === false;
  const canShowBalance = accountRestrictionsAvailable || versionChanged === true;
  const insufficient = accountRestrictionsAvailable && balance < cost;

  const handleQualitySelect = useCallback(
    (nextKey) => {
      const next = QUALITY_OPTIONS.find((q) => q.key === nextKey);
      if (!next) return;
      if (!next.allowedPlans.includes(userPlan)) {
        onClose?.();
        try {
          window.dispatchEvent(
            new CustomEvent('app:insufficient-access', {
              detail: {
                message: t('photoToVideo.premiumRequired'),
              },
            }),
          );
        } catch {
          navigate('/plans');
        }
        return;
      }
      setSelectedKey(nextKey);
    },
    [userPlan, navigate, onClose, t],
  );

  const performSubmit = useCallback(async () => {
    if (!srcGenerationId) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await photoToVideoMutation.mutateAsync({
        srcGenerationId,
        quality: quality.enum,
        durationSec,
      });
      const { generationId } = res;
      onClose?.();
      if (typeof onSubmitted === 'function') {
        onSubmitted(generationId);
      } else {
        navigate('/gallery');
      }
    } catch (e) {
      const status = e?.response?.status;
      if (status === 402) {
        setError(t('photoToVideo.insufficientCredits'));
      } else if (status === 403) {
        setError(t('photoToVideo.qualityNotAllowed'));
      } else {
        setError(t('photoToVideo.startError'));
      }
      setSubmitting(false);
    }
  }, [
    srcGenerationId,
    quality.enum,
    durationSec,
    navigate,
    onClose,
    onSubmitted,
    photoToVideoMutation,
    t,
  ]);

  const handleSubmit = useCallback(() => {
    requireConsent(performSubmit, 'generate');
  }, [performSubmit, requireConsent]);

  if (!open) return null;
  const creditForms = [
    t('prompt.credit'),
    t('prompt.credits234'),
    t('prompt.credits5'),
  ];
  const costCreditsWord = pluralize(cost, creditForms);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center"
      role="dialog"
      aria-modal="true"
      aria-label={t('viewer.animatePhoto')}
      data-testid="photo-to-video-sheet"
    >
      <button
        type="button"
        aria-label={t('common.close')}
        data-testid="photo-to-video-backdrop"
        onClick={onClose}
        className="absolute inset-0 bg-black/[0.64]"
      />

      <div
        className="relative w-full max-w-md rounded-t-3xl bg-popup-gradient p-6 flex flex-col gap-4"
        onClick={(e) => e.stopPropagation()}
        style={{
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 24px)',
        }}
      >
        <button
          type="button"
          aria-label={t('common.close')}
          data-testid="photo-to-video-close"
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full text-text-secondary"
        >
          <Icon name="clear" size={20} className="text-text-secondary" />
        </button>

        <div className="flex flex-col items-center gap-1 px-8">
          <h2 className="font-sans font-bold text-h2 text-text-primary text-center">
            {t('photoToVideo.title')}
          </h2>
          <p className="font-sans text-plan text-text-secondary text-center">
            {t('photoToVideo.subtitle')}
          </p>
        </div>

        <div className="flex flex-col gap-3">
          {QUALITY_OPTIONS.map((opt) => (
            <QualityCard
              key={opt.key}
              option={opt}
              selected={selectedKey === opt.key}
              onSelect={() => handleQualitySelect(opt.key)}
            />
          ))}
        </div>

        {error && (
          <p
            role="alert"
            data-testid="photo-to-video-error"
            className="font-sans text-chip-label text-rose-400 text-center"
          >
            {error}
          </p>
        )}

        <UIButton
          type="primary"
          size="lg"
          onClick={handleSubmit}
          disabled={
            submitting
            || (accountRestrictionsAvailable && (!planAllowsQuality || insufficient))
          }
          className="w-full bg-primary-btn-gradient mt-4"
          data-testid="photo-to-video-submit"
          aria-label={`${t('viewer.animatePhoto')} ${cost} ${costCreditsWord}`}
        >
          <span className="flex flex-col items-center gap-0.5 leading-tight">
            <span
              className="font-sans font-bold"
              data-testid="photo-to-video-cost"
            >
              {submitting
                ? t('photoToVideo.starting')
                : insufficient
                  ? t('photoToVideo.needCredits', { cost, creditsWord: costCreditsWord, balance })
                  : t('photoToVideo.animateFor', { cost, creditsWord: costCreditsWord })}
            </span>
            {!submitting && !insufficient && canShowBalance && (
              <span
                className="font-sans text-chip-label inline-flex items-center gap-1 opacity-80"
                data-testid="photo-to-video-balance"
              >
                {t('photoToVideo.balance', { balance })}
                <Icon name="coin" size={12} />
              </span>
            )}
          </span>
        </UIButton>
      </div>
    </div>
  );
}

function QualityCard({ option, selected, onSelect }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      data-testid={`photo-to-video-quality-${option.key.toLowerCase()}`}
      className={[
        'w-full flex items-center gap-4 px-4 py-3 rounded-home-16 text-left transition',
        'bg-glass-8',
        selected
          ? 'border-2 border-accent-orange-start'
          : 'border border-glass-16',
      ].join(' ')}
    >
      <span
        aria-hidden
        className={[
          'shrink-0 w-12 h-12 rounded-full flex items-center justify-center font-sans font-bold text-plan',
          selected
            ? 'bg-accent-orange-start/30 text-accent-orange-start'
            : 'bg-glass-16 text-text-secondary',
        ].join(' ')}
      >
        {option.key}
      </span>
      <span className="flex-1 flex flex-col">
        <span className="font-sans font-bold text-h2 text-text-primary">
          {t(option.titleKey)}
        </span>
        <span className="font-sans text-plan text-text-secondary">
          {t(option.subtitleKey)}
        </span>
      </span>
      <span
        aria-hidden
        className={[
          'shrink-0 w-6 h-6 rounded-full flex items-center justify-center',
          selected
            ? 'bg-accent-orange-start text-text-primary'
            : 'border-2 border-glass-16',
        ].join(' ')}
      >
        {selected && <Icon name="check" size={14} />}
      </span>
    </button>
  );
}
